import { createFileRoute, Link } from "@tanstack/react-router";
import { buildHead } from "@/lib/seo";
import { AGENCY } from "@/lib/agency";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const UPDATED = "June 25, 2026";

export const Route = createFileRoute("/disclosures")({
  head: () =>
    buildHead({
      title: `Disclosures - ${AGENCY.name}`,
      description: `Important disclosures for ${AGENCY.name}: an independent business financing advisory, not a direct lender. Rates, terms, and approval are determined by third-party lenders.`,
      path: "/disclosures",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Disclosures", path: "/disclosures" },
      ],
    }),
  component: DisclosuresPage,
});

function DisclosuresPage() {
  return (
    <LegalLayout
      title="Disclosures"
      updated={UPDATED}
      intro={`These disclosures explain how ${AGENCY.name} works and the limits of the information on this website. Please read them together with our Terms of Use and Privacy Policy.`}
    >
      <LegalSection heading="We are an advisory, not a lender">
        <p>
          {AGENCY.name} is an independent business financing advisory based in{" "}
          {AGENCY.city}. We are not a bank or a direct lender. We help business
          owners compare and apply for financing through a network of
          third-party lenders and funding partners. All loan products, credit
          decisions, rates, fees, and terms are offered and determined solely by
          those lenders, not by {AGENCY.name}.
        </p>
      </LegalSection>

      <LegalSection heading="No guarantee of approval, amount, or terms">
        <p>
          Contacting us or submitting an inquiry does not guarantee an offer,
          approval, or any specific loan amount, rate, term, or funding
          timeline. Every request is subject to each lender's underwriting,
          approval, and final documentation. We cannot and do not promise that
          financing will be available for any particular business.
        </p>
      </LegalSection>

      <LegalSection heading="Rates, amounts, and timelines are examples only">
        <p>
          Any loan amounts, ranges, repayment terms, funding speeds, or
          qualifications shown on this site are typical examples for
          illustration only. They are not an offer, a quote, or a commitment to
          lend. Actual terms depend on the lender and on your business profile,
          including revenue, time in business, credit, and industry. Programs and
          pricing change over time.
        </p>
      </LegalSection>

      <LegalSection heading="Not financial, legal, or tax advice">
        <p>
          The content on this website is provided for general informational
          purposes only. It is not financial, legal, accounting, or tax advice,
          and it does not take your specific circumstances into account. You
          should consult a qualified professional before making any borrowing or
          business decision.
        </p>
      </LegalSection>

      <LegalSection heading="How we are compensated">
        <p>
          We do not charge business owners an upfront fee to review a file or
          shop offers. When a lender funds a deal and you accept the terms,{" "}
          {AGENCY.name} may be compensated by that lender. This can create an
          incentive to place financing; we work to present options that fit your
          needs, but you should always review each offer's full terms before
          accepting.
        </p>
      </LegalSection>

      <LegalSection heading="Third-party names, links, and trademarks">
        <p>
          Lender names, product names, and trademarks referenced on this site
          are the property of their respective owners and are used for
          identification only. Their mention does not imply endorsement or
          partnership unless expressly stated. External links are provided for
          convenience; we do not control and are not responsible for third-party
          content.
        </p>
      </LegalSection>

      <LegalSection heading="Accuracy of information">
        <p>
          We work to keep this site accurate and current, but it is provided
          "as is" without warranties of any kind. Information may contain errors
          or become out of date. Verify any detail that matters to your decision
          directly with us or with the lender.
        </p>
      </LegalSection>

      <LegalSection heading="Questions">
        <p>
          If you have any questions about these disclosures, call us at{" "}
          <a href={AGENCY.phoneHref} className="font-medium text-foreground hover:text-accent">
            {AGENCY.phone}
          </a>{" "}
          or visit our{" "}
          <Link to="/contact" className="font-medium text-foreground hover:text-accent">
            contact page
          </Link>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
